import { forwardRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { TextField, TextFieldProps } from '@mui/material';

import { processCurrencyInput } from 'src/utils/currency';

// ----------------------------------------------------------------------

interface RHFCurrencyFieldProps extends Omit<TextFieldProps, 'name'> {
  name: string;
}

const RHFCurrencyField = forwardRef<HTMLInputElement, RHFCurrencyFieldProps>(
  ({ name, helperText, ...other }, ref) => {
    const { control } = useFormContext();

    const {
      field,
      fieldState: { error },
    } = useController({
      name,
      control,
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const { newValue, preventDefault } = processCurrencyInput(event, field.value);

      if (preventDefault) {
        event.preventDefault();
        field.onChange(newValue);
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // This handles paste events and other input methods
      const inputValue = event.target.value;

      // If it's a paste or other input, format it
      if (inputValue !== field.value) {
        // Remove all non-digit characters and format
        const digits = inputValue.replace(/\D/g, '');
        if (digits) {
          const cents = parseInt(digits, 10);
          const dollars = cents / 100;
          field.onChange(`$${dollars.toFixed(2)}`);
        } else {
          field.onChange('$0.00');
        }
      }
    };

    return (
      <TextField
        {...field}
        ref={ref}
        fullWidth
        error={!!error}
        helperText={error ? error?.message : helperText}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...other}
      />
    );
  }
);

RHFCurrencyField.displayName = 'RHFCurrencyField';

export default RHFCurrencyField;
